/**
 * Low-battery auto-dock settings.
 *
 * Peribott Dynamic LLP — Author: Amit Jagnani
 *
 * v6.3.50. The "Robot supports auto-docking" switch is a CAPABILITY
 * declaration, not a feature toggle: most robots in the fleet cannot dock
 * themselves yet. Everything else stays disabled until it is on, and the
 * server refuses to dispatch to a robot without it — so enabling auto-dock
 * can never make a non-docking robot drive off looking for a charger.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Switch, FormControlLabel, Slider, TextField, Button,
  Alert, Divider, CircularProgress, Chip,
} from '@mui/material';
import { BatteryChargingFull, Save } from '@mui/icons-material';
import apiClient from '../../utils/apiClient';

interface Props {
  robotId: string;
  /** Waypoint labels known for the robot's current map, if loaded. */
  waypoints?: string[];
}

type Cfg = {
  supported: boolean;
  enabled: boolean;
  threshold: number;
  resume_at: number;
  waypoint: string;
  cooldown_minutes: number;
};

const AutoDockSettings: React.FC<Props> = ({ robotId, waypoints = [] }) => {
  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/robots/${robotId}/autodock`);
      const { robot_serial, ...rest } = r.data || {};
      setCfg(rest as Cfg);
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Could not load auto-dock settings' });
    } finally {
      setLoading(false);
    }
  }, [robotId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    setMsg(null);
    try {
      const r = await apiClient.put(`/robots/${robotId}/autodock`, cfg);
      const { success, robot_serial, ...rest } = r.data || {};
      setCfg(rest as Cfg);
      setMsg({ type: 'success', text: 'Auto-dock settings saved' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>;
  }
  if (!cfg) {
    return <Alert severity="error">{msg?.text || 'Auto-dock settings unavailable'}</Alert>;
  }

  const gated = !cfg.supported;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BatteryChargingFull fontSize="small" />
        <Typography variant="h6">Low-battery auto-dock</Typography>
        {cfg.supported && cfg.enabled && <Chip size="small" color="success" label="Active" />}
      </Box>

      {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={cfg.supported}
            onChange={(e) => setCfg({ ...cfg, supported: e.target.checked, enabled: e.target.checked ? cfg.enabled : false })}
          />
        }
        label="This robot supports auto-docking"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Turn this on only for robots that physically have a charging dock and a
        charger waypoint on their map. While it is off, the server will never
        send this robot to a charger.
      </Typography>

      {gated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Auto-dock is unavailable for this robot until it is marked as
          supporting docking.
        </Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ opacity: gated ? 0.5 : 1, pointerEvents: gated ? 'none' : 'auto' }}>
        <FormControlLabel
          control={
            <Switch
              checked={cfg.enabled}
              disabled={gated}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            />
          }
          label="Automatically send to charger on low battery"
        />

        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography variant="body2">Send to charger at or below: <b>{cfg.threshold}%</b></Typography>
          <Slider
            value={cfg.threshold}
            onChange={(_, v) => {
              const t = v as number;
              setCfg({ ...cfg, threshold: t, resume_at: Math.max(cfg.resume_at, t + 15) });
            }}
            min={5} max={90} step={1} disabled={gated}
            marks={[{ value: 10, label: '10%' }, { value: 20, label: '20%' }, { value: 50, label: '50%' }]}
          />
        </Box>

        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography variant="body2">Re-arm once battery recovers above: <b>{cfg.resume_at}%</b></Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Prevents the robot being re-commanded over and over while it sits
            just below the trigger level. Must stay above the trigger.
          </Typography>
          <Slider
            value={cfg.resume_at}
            onChange={(_, v) => setCfg({ ...cfg, resume_at: Math.max(v as number, cfg.threshold + 5) })}
            min={10} max={100} step={1} disabled={gated}
          />
        </Box>

        {waypoints.length > 0 ? (
          <TextField
            select SelectProps={{ native: true }}
            label="Charger waypoint" size="small" fullWidth disabled={gated}
            value={cfg.waypoint}
            onChange={(e) => setCfg({ ...cfg, waypoint: e.target.value })}
            sx={{ mt: 2 }} InputLabelProps={{ shrink: true }}
          >
            {!waypoints.includes(cfg.waypoint) && <option value={cfg.waypoint}>{cfg.waypoint}</option>}
            {waypoints.map((w) => <option key={w} value={w}>{w}</option>)}
          </TextField>
        ) : (
          <TextField
            label="Charger waypoint" size="small" fullWidth disabled={gated}
            value={cfg.waypoint}
            onChange={(e) => setCfg({ ...cfg, waypoint: e.target.value })}
            helperText="Name of the waypoint on this robot's map that sits at the dock"
            sx={{ mt: 2 }}
          />
        )}

        <TextField
          label="Minimum minutes between attempts" type="number" size="small"
          disabled={gated} value={cfg.cooldown_minutes}
          onChange={(e) => setCfg({ ...cfg, cooldown_minutes: Number(e.target.value) || 15 })}
          inputProps={{ min: 1, max: 240 }}
          helperText="Stops repeated attempts if the robot can't reach the dock"
          sx={{ mt: 2 }} fullWidth
        />
      </Box>

      <Button variant="contained" startIcon={<Save />} onClick={save}
        disabled={saving} sx={{ mt: 3 }}>
        {saving ? 'Saving…' : 'Save auto-dock settings'}
      </Button>

      <Alert severity="warning" sx={{ mt: 3 }}>
        Auto-dock issues a normal navigation command to the charger waypoint.
        It does not override a manual drive already in progress, and it will
        not act while the robot is charging or offline.
      </Alert>
    </Box>
  );
};

export default AutoDockSettings;
